import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchGraphQL } from '../../../lib/graphql';
import {
  Container,
  Section,
  Text,
  Stack,
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  LoadingSpinner,
  Alert,
  AlertTitle,
  AlertDescription,
  EmptyState
} from '@tonic/shared/components/ui';
import {
  Search,
  Plus,
  Filter,
  Settings,
  Activity,
  ExternalLink,
  AlertCircle,
  Box,
  Clock
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const STATUS_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' },
  { label: 'Draft', value: 'DRAFT' }
];

const CHANNEL_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Web Chat', value: 'WEB' },
  { label: 'In-store Kiosk', value: 'KIOSK' },
  { label: 'Voice', value: 'VOICE' }
];

const GET_AGENTS = `
  query GetAgents($search: String, $filter: AgentFilter) {
    agentsList(search: $search, filter: $filter) {
      items {
        id
        name
        description
        status
        channel
        lastActive
        metrics {
          conversations
          avgRating
          successRate
        }
      }
      totalCount
    }
  }
`;

export function AgentDashboardPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: null,
    channel: null
  });

  const { isLoading: loading, error, data } = useQuery({
    queryKey: ['agents', searchQuery, filters],
    queryFn: () => fetchGraphQL(GET_AGENTS, {
      search: searchQuery || undefined,
      filter: {
        status: filters.status === 'all' ? null : filters.status,
        channel: filters.channel === 'all' ? null : filters.channel
      }
    })
  });

  const handleCreateAgent = () => {
    navigate('agents/new');
  };

  const handleAgentClick = (id) => {
    navigate(`agents/${id}`);
  };

  const handleViewLogs = (e, id) => {
    e.stopPropagation();
    navigate(`agents/${id}/logs`);
  };

  const handleToggleStatus = async (e, id, currentStatus) => {
    e.stopPropagation();
    // TODO: Implement status toggle mutation
    console.log('Toggle status:', id, currentStatus);
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'default';
      case 'DRAFT':
        return 'secondary';
      case 'DISABLED':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  return (
    <Container>
      <Section>
        <Stack space={6}>
          <Stack direction="row" className="items-center justify-between">
            <Stack space={2}>
              <Text variant="h1">AI Agents</Text>
              <Text variant="body" className="text-muted-foreground">
                Manage all AI agents that interact with customers online and in store.
              </Text>
            </Stack>
            <Button onClick={handleCreateAgent}>
              <Plus className="h-4 w-4 mr-2" />
              New Agent
            </Button>
          </Stack>

          <Card>
            <CardHeader>
              <CardTitle>Search & Filter</CardTitle>
            </CardHeader>
            <CardContent>
              <Stack space={4}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search agents..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>

                <Stack direction="row" space={4}>
                  <div className="flex-1">
                    <Text variant="small" className="font-medium mb-1.5">Status</Text>
                    <Select
                      value={filters.status || ''}
                      onValueChange={(value) => setFilters(prev => ({
                        ...prev,
                        status: value || null
                      }))}
                    >
                      <SelectTrigger>
                        {STATUS_OPTIONS.find(opt => opt.value === (filters.status || ''))?.label || 'All'}
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Text variant="small" className="font-medium mb-1.5">Channel</Text>
                    <Select
                      value={filters.channel || ''}
                      onValueChange={(value) => setFilters(prev => ({
                        ...prev,
                        channel: value || null
                      }))}
                    >
                      <SelectTrigger>
                        {CHANNEL_OPTIONS.find(opt => opt.value === (filters.channel || ''))?.label || 'All'}
                      </SelectTrigger>
                      <SelectContent>
                        {CHANNEL_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {loading && (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                {error.message || 'Failed to load agents'}
              </AlertDescription>
            </Alert>
          )}

          {data?.agentsList && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.agentsList.items.map((agent) => (
                <Card
                  key={agent.id}
                  onClick={() => handleAgentClick(agent.id)}
                  className="cursor-pointer hover:border-primary transition-colors"
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <Text variant="body" className="font-medium line-clamp-1">{agent.name}</Text>
                        <Text variant="small" className="text-muted-foreground line-clamp-1">{agent.description}</Text>
                      </div>
                      <Stack direction="row" className="items-center gap-1 flex-shrink-0">
                        <Badge variant={getStatusBadgeVariant(agent.status)} className="text-xs">
                          {agent.status.charAt(0) + agent.status.slice(1).toLowerCase()}
                        </Badge>
                        <Badge variant="secondary" className="text-xs">{agent.channel}</Badge>
                      </Stack>
                    </div>

                    <div className="flex items-center justify-between mt-2 text-sm">
                      <div className="flex items-center gap-3">
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={(e) => handleViewLogs(e, agent.id)}
                          className="h-7 px-2"
                        >
                          <Activity className="h-3 w-3 mr-1" />
                          Logs
                        </Button>
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={(e) => handleToggleStatus(e, agent.id, agent.status)}
                          className="h-7 px-2"
                        >
                          <Settings className="h-3 w-3 mr-1" />
                          Settings
                        </Button>
                      </div>
                      <div className="flex items-center text-muted-foreground">
                        <Clock className="h-3 w-3 mr-1" />
                        <Text variant="small">
                          {formatDistanceToNow(new Date(agent.lastActive), { addSuffix: true })}
                        </Text>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {data?.agentsList?.totalCount === 0 && (
            <EmptyState
              icon={Box}
              title="No agents found"
              description="Create a new agent to get started"
              action={
                <Button onClick={handleCreateAgent}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Agent
                </Button>
              }
            />
          )}
        </Stack>
      </Section>
    </Container>
  );
}
